import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/hooks';
import {
  ThunkCreateTicketFromTemplate,
  ThunkGetTicketTemplate,
} from 'store/ticketTemplates/thunks';
import { selectClientPortalConfigurations } from 'store/ticketTemplates/selectors';

import { ITicketTemplate } from 'services/ticketTemplates/types';

import { TICKET } from 'router/routes';

import { createQuestionsValidationSchema } from 'pages/TicketTemplate/form';

import Loader from 'components/Loader';
import Button from 'components/Button/Button';
import FormSelect from 'components/Select/FormSelect';
import TicketTemplateItem from 'pages/TicketTemplate/components/TicketTemplateItem';
import TicketCreationDefaultForm from 'components/CreateTicketDialog/components/TicketCreationDefaultForm';

import useStyles from '../styles';

interface IFormValues {
  companyId?: string;
  configurationId?: string;
  ticketTemplateId: number | undefined;
  questions: Array<{ id: number; value: unknown }>;
}

interface IProps {
  ticketTemplateId: number;
  onClose: () => void;
  onSuccess?: () => void;
  isConnectWise: boolean;
  notUserRole: boolean;
}

const TemplateHandler: React.FC<IProps> = ({
  ticketTemplateId,
  onClose,
  onSuccess,
  isConnectWise,
  notUserRole,
}) => {
  const [loading, setLoading] = useState(true);
  const [ticketTemplate, setTicketTemplate] = useState<ITicketTemplate>();
  const [processing, setProcessing] = useState(false);

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const configurationOptions = useSelector(selectClientPortalConfigurations);
  const companyOptions = useSelector(selectClientPortalConfigurations);

  const initialQuestions = useMemo(() => {
    return Object.fromEntries(ticketTemplate?.questions.map(item => [item.id, '']) ?? []) ?? {};
  }, [ticketTemplate]);

  const formik = useFormik({
    initialValues: {
      questions: initialQuestions,
      configurationId: undefined,
      companyId: undefined,
    },
    initialErrors: {
      questions: {},
    },
    onSubmit: values => {
      setProcessing(true);
      const payload: IFormValues = {
        questions: Object.entries(values.questions).map(i => ({ id: Number(i[0]), value: i[1] })),
        ticketTemplateId: ticketTemplate?.id,
        configurationId: values.configurationId,
        companyId: values.companyId,
      };

      dispatch(ThunkCreateTicketFromTemplate({ payload }))
        .unwrap()
        .then(res => {
          if (onSuccess) {
            onSuccess();
          }
          navigate(TICKET.replace(':ticketId', res.data));
          onClose()
        })
        .finally(() => {
          setProcessing(false);
        });
    },
    validationSchema: createQuestionsValidationSchema(ticketTemplate),
    enableReinitialize: true,
  });

  const onMount = useCallback(() => {
    if (!ticketTemplateId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    dispatch(ThunkGetTicketTemplate({ ticketTemplateId }))
      .unwrap()
      .then(res => {
        setTicketTemplate(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [ticketTemplateId]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) {
    return (
      <div className={classes.loaderWrapper}>
        <Loader />
        <div className={classes.buttonsContainer}>
          <Button text="Close" onClick={onClose} />
        </div>
      </div>
    );
  }
  if (ticketTemplateId === 0) {
    return (
      <TicketCreationDefaultForm
        isConnectWise={isConnectWise}
        onClose={onClose}
        onSuccess={onSuccess}
        notUserRole={notUserRole}
      />
    );
  }
  return (
    <form onSubmit={formik.handleSubmit} className={classes.form}>
      <div className={classes.templateBody}>
        {isConnectWise && (
          <>
            {!notUserRole && ticketTemplate?.showConfigurationItemsDropdown && (
              <FormSelect
                formik={formik}
                name="configurationId"
                label="Select Configuration"
                defaultValue=""
                options={configurationOptions}
              />
            )}
            {notUserRole && !!companyOptions.length && (
              <FormSelect
                formik={formik}
                name="companyId"
                label="Select Customer"
                defaultValue=""
                options={companyOptions}
              />
            )}
          </>
        )}
        {ticketTemplate?.questions.map(q => (
          <TicketTemplateItem key={q.id} {...q} formik={formik} disabled={processing} />
        ))}
      </div>
      <div className={classes.buttonsContainer}>
        <Button text="Close" onClick={onClose} disabled={processing} />
        <Button text="Create" type="submit" loading={processing} disabled={processing} />
      </div>
    </form>
  );
};

export default TemplateHandler;
